import React from 'react';
import Button from '../../../shared/components/Button';
import { formatCurrency } from '../../../shared/utils/formatCurrency';
import { formatDate } from '../../../shared/utils/formatDate';

const SuccessScreen = ({ data, onInvite, groupId, onSkip }) => {
  const culturalTermLabel = {
    esusu: 'Esusu',
    ajo: 'Ajo',
    sol: 'Sol',
    'sòl': 'Sòl',
    susu: 'Susu',
    partner: 'Partner',
    tanda: 'Tanda',
    other: 'Other'
  };

  const frequencyLabel = {
    weekly: 'Weekly',
    'bi-weekly': 'Bi-weekly',
    monthly: 'Monthly'
  };

  return (
    <div className="flex flex-col min-h-screen px-4 py-8 bg-gradient-to-b from-neutral-100 to-neutral-50">
      {/* Success Header */}
      <div className="text-center mb-8">
        <div className="w-20 h-20 bg-success-600 rounded-full flex items-center justify-center mb-4 mx-auto">
          <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h1 className="text-3xl font-bold text-neutral-900 mb-2">
          Your group is ready ✓
        </h1>
        <p className="text-lg text-neutral-600">
          Your {culturalTermLabel[data.culturalTerm] || 'group'} has been created successfully
        </p>
      </div>

      {/* Group Summary */}
      <div className="max-w-md w-full mx-auto space-y-4">
        <div className="bg-neutral-100 rounded-lg shadow-sm border border-neutral-200 p-6">
          <h2 className="text-lg font-semibold text-neutral-900 mb-4">Group Summary</h2>
          
          <div className="space-y-3">
            {/* Group Name */}
            <div className="flex justify-between items-center py-2 border-b border-neutral-100">
              <span className="text-sm text-neutral-600">Group Name</span>
              <span className="text-sm font-medium text-neutral-900">{data.groupName}</span>
            </div>
            
            {/* Cultural Term */}
            <div className="flex justify-between items-center py-2 border-b border-neutral-100">
              <span className="text-sm text-neutral-600">Cultural Term</span>
              <span className="text-sm font-medium text-neutral-900">
                {culturalTermLabel[data.culturalTerm] || 'Other'}
              </span>
            </div>
            
            {/* Contribution Amount */}
            <div className="flex justify-between items-center py-2 border-b border-neutral-100">
              <span className="text-sm text-neutral-600">Contribution per Hand</span>
              <span className="text-sm font-medium text-neutral-900">
                {formatCurrency(data.amount)}
              </span>
            </div>
            
            {/* Frequency */}
            <div className="flex justify-between items-center py-2 border-b border-neutral-100">
              <span className="text-sm text-neutral-600">Frequency</span>
              <span className="text-sm font-medium text-neutral-900">
                {frequencyLabel[data.frequency]}
              </span>
            </div>
            
            {/* Start Date */}
            <div className="flex justify-between items-center py-2">
              <span className="text-sm text-neutral-600">First Contribution</span>
              <span className="text-sm font-medium text-neutral-900">
                {data.startDate ? formatDate(data.startDate, 'short') : 'Not set'}
              </span>
            </div>
          </div>
        </div>

        {/* What's Next */}
        <div className="bg-primary-50 rounded-lg p-4">
          <h3 className="text-sm font-medium text-primary-900 mb-2">What's next?</h3>
          <ul className="text-xs text-primary-800 space-y-1">
            <li>• Invite members to join your group</li>
            <li>• Set up payment methods for contributions</li>
            <li>• Configure reminder settings</li>
            <li>• Start tracking contributions</li>
          </ul>
        </div>

        {/* Group ID */}
        <div className="bg-neutral-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-neutral-600 mb-1">Group ID (for sharing)</p>
              <p className="text-sm font-mono font-medium text-neutral-900">{groupId || 'GRP-XXXXX'}</p>
            </div>
            <button className="text-primary-600 hover:text-primary-700">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Action Button */}
      <div className="mt-8 max-w-md w-full mx-auto">
        <Button
          onClick={() => groupId && onInvite(`/admin/group/${groupId}/add-member`)}
          variant="primary"
          size="lg"
          className="w-full text-lg py-4"
        >
          Invite Your Members →
        </Button>
        
        <div className="mt-4 text-center">
          <button 
            onClick={() => groupId && onSkip()}
            className="text-sm text-neutral-600 hover:text-neutral-800"
          >
            Skip for now
          </button>
        </div>
      </div>

      {/* Security Note */}
      <div className="mt-8 text-center">
        <p className="text-xs text-neutral-500">
          Your group information is secure and never shared with third parties
        </p>
      </div>
    </div>
  );
};

export default SuccessScreen;
